import { z } from 'zod';
import type { CreateScrapingIntentRequest } from '../apis';

export enum SCRAPING_INTENT_MODE {
  ONETIME = 'onetime',
  PERIODIC = 'periodic',
}

export enum SCRAPING_INTENT_SELECTOR_TYPE {
  KEYWORD = 'keyword',
  URLS = 'urls',
}

export enum SCRAPING_INTENT_SOURCE_TYPE {
  ADMIN = 'admin',
  QUERY = 'query',
}

export enum SCRAPING_INTENT_STATUS {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum SCRAPING_RUN_STATUS {
  PENDING = 'pending',
  SUBMITTING = 'submitting',
  SUBMITTED = 'submitted',
  SUBMIT_FAILED = 'submit_failed',
  CANCEL_REQUESTED = 'cancel_requested',
  CANCELED_BEFORE_SUBMIT = 'canceled_before_submit',
  LIBRARIAN_PENDING = 'PENDING',
  RUNNING_VALIDATING = 'RUNNING_VALIDATING',
  RUNNING_PRE_SCRAPING = 'RUNNING_PRE_SCRAPING',
  RUNNING_SUBMITTING = 'RUNNING_SUBMITTING',
  RUNNING_SCRAPING = 'RUNNING_SCRAPING',
  RUNNING_REGISTERING_RESULT = 'RUNNING_REGISTERING_RESULT',
  RUNNING_ANALYZING = 'RUNNING_ANALYZING',
  RUNNING_INDEXING = 'RUNNING_INDEXING',
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  CANCELING = 'CANCELING',
  CANCELED = 'CANCELED',
}

export const DateRangeSchema = z.object({
  start_date: z.iso.datetime(),
  end_date: z.iso.datetime(),
});

export type DateRange = {
  start_date: string;
  end_date: string;
};

export type ScrapingIntentLimit = {
  item_per_day: number;
  date_range?: DateRange;
};

export type ScrapingIntentSource = {
  type: SCRAPING_INTENT_SOURCE_TYPE;
  id?: string | null;
};

export type ScrapingIntentTimezone = string;

export type ScrapingIntentListResponse = {
  items: ScrapingIntentSummary[];
  page: ScrapingPage;
};

export type ScrapingIntentSummary = {
  intent_id: string;
  name?: string | null;
  mode: SCRAPING_INTENT_MODE;
  timezone: ScrapingIntentTimezone;
  source: ScrapingIntentSource;
  payload: CreateScrapingIntentRequest['payload'];
  status: SCRAPING_INTENT_STATUS;
  created_at: string;
  latest_run?: ScrapingRunSummary | null;
  stopped_at?: string | null;
};

export type ScrapingRunListResponse = {
  items: ScrapingRunSummary[];
  page: ScrapingPage;
};

export type ScrapingRunSummary = {
  run_id: string;
  intent_id: string;
  execution_date: string;
  status: SCRAPING_RUN_STATUS;
  created_at: string;
  completed_at?: string | null;
  canceled_at?: string | null;
};

export type CancelScrapingRunResponse = {
  run_id: string;
  status: string;
  cancel_requested_at: string;
};

export type CancelScrapingRunRequest = {
  reason?: string;
};

export type ScrapingPage = {
  limit: number;
  next_cursor: string | null;
  has_more: boolean;
  total_count: number;
};

export type ScrapingIntentPage = ScrapingPage;

export type ScrapingRunPage = ScrapingPage;

export type ScrapingRunResultsResponse = {
  run_id: string;
  pipeline_type: string;
  requester_type: string;
  request_status: string;
  data_type: string;
  items: ScrapingRunResultItem[];
  page: ScrapingPage;
};

export type ScrapingRunResultItem = PostResultItem | ReviewResultItem;

export type PostResultItem = {
  id: string;
  platform?: string;
  url?: string;
  author_id?: string;
  author_username?: string;
  text?: string;
  posted_at?: string;
  collected_at?: string;
  comments: SocialComment[];
  [key: string]: unknown;
};

export type ReviewResultItem = {
  id: string;
  platform: string;
  title: string | null;
  content: string | null;
  rating: number | null;
  created_at: string;
  updated_at: string | null;
  author_name: string;
  verified_purchase: boolean | null;
  product: ReviewProduct;
  engagement: ReviewEngagement;
  media: ReviewMedia;
  scraped_at: string;
};

export type SocialComment = {
  id: string;
  post_id: string;
  post_url?: string | null;
  parent_id?: string | null;
  content?: string | null;
  created_at?: string | null;
  author: {
    id: string;
    username: string;
  };
  scraped_at: string;
};

export type ReviewProduct = {
  option_id: string | null;
  id: string;
  url: string;
  name: string;
  option_name: string | null;
};

export type ReviewEngagement = {
  helpful_count: number | null;
  unhelpful_count: number | null;
  like_count: number | null;
};

export type ReviewMedia = {
  image_urls: string[];
  video_urls: string[];
};

export type ErrorResponse = {
  error?: string;
  message?: string;
  code?: string | null;
};
